import apiClient from './api';
import type {
  Screenshot,
  ScreenshotListResponse,
  ScreenshotFilter,
  CaptureScreenshotResponse,
  DeviceCommand,
} from '../types';

export const screenshotService = {
  // Trigger screenshot capture on device. Mode is selected by the caller:
  //   waitForAck=true  → sync; HTTP blocks until the device ACKs.
  //   waitForAck=false → async; server returns immediately with screenshot_id.
  // The flag should mirror the server's `/info` → `capture.wait_for_ack` so
  // the UI's response handling matches how the server actually behaved.
  capture: async (deviceId: string, waitForAck = false): Promise<CaptureScreenshotResponse> => {
    const url = waitForAck
      ? `/devices/${deviceId}/screenshots/capture`
      : `/devices/${deviceId}/screenshots/capture?noWait=true`;
    return apiClient.post<CaptureScreenshotResponse>(
      url,
      {},
      // Generous ceiling for the sync path (server default 30s MQTT
      // wait). Harmless in async mode since the server returns quickly.
      { timeout: 60_000 },
    );
  },

  // List screenshots for a device
  list: async (deviceId: string, filter?: ScreenshotFilter): Promise<ScreenshotListResponse> => {
    const params = new URLSearchParams();
    if (filter?.from) params.append('from', filter.from);
    if (filter?.to) params.append('to', filter.to);
    if (filter?.limit) params.append('limit', filter.limit.toString());
    if (filter?.offset) params.append('offset', filter.offset.toString());

    const qs = params.toString();
    return apiClient.get<ScreenshotListResponse>(
      `/devices/${deviceId}/screenshots${qs ? `?${qs}` : ''}`
    );
  },

  // Get single screenshot with signed download URL
  get: async (deviceId: string, screenshotId: string): Promise<Screenshot> => {
    return apiClient.get<Screenshot>(
      `/devices/${deviceId}/screenshots/${screenshotId}`
    );
  },

  // Delete a screenshot
  delete: async (deviceId: string, screenshotId: string): Promise<void> => {
    return apiClient.delete(`/devices/${deviceId}/screenshots/${screenshotId}`);
  },

  // Bulk capture screenshots for multiple devices
  bulkCapture: async (deviceIds: string[]): Promise<{ success: boolean; results: CaptureScreenshotResponse[] }> => {
    return apiClient.post<{ success: boolean; results: CaptureScreenshotResponse[] }>(
      `/screenshots/bulk-capture`,
      { device_ids: deviceIds }
    );
  },

  // Get a single device-command lifecycle row by id. Used by the
  // screen-capture detail panel to render state_history + initiator.
  getCommand: async (commandId: string): Promise<DeviceCommand> => {
    return apiClient.get<DeviceCommand>(`/device-commands/${commandId}`);
  },

  // Get global per-status counts for filter-chip badges.
  getCounts: async (deviceId: string): Promise<{
    total: number;
    verified: number;
    in_progress: number;
    failed: number;
  }> => {
    return apiClient.get(`/devices/${deviceId}/screenshots/counts`);
  },

  // Fetch a freshly-signed download URL (with Content-Disposition:
  // attachment) for a screenshot. The backend mints a parallel URL
  // for download purposes — the inline-display URL returned by list/
  // get omits Content-Disposition deliberately so the gallery shows
  // images instead of triggering downloads.
  getDownloadURL: async (
    deviceId: string,
    screenshotId: string,
  ): Promise<{ url: string; filename: string }> => {
    return apiClient.get<{ url: string; filename: string }>(
      `/devices/${deviceId}/screenshots/${screenshotId}/download`,
    );
  },
};

export default screenshotService;
